import { reactMount } from '@/lib/reactMount'
import { PlusIcon } from '@heroicons/react/24/solid'
import { useRef, useState, useEffect } from 'react'
import { Button } from '@/components/Button'

const FAQBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'dark'
  const backgroundColor = umbraco.BlockBackground ? `#${umbraco.BlockBackground}` : '#ffffff'

  const [openIndex, setOpenIndex] = useState(null)
  const questionRefs = useRef([])

  // Check URL hash on component mount and open matching question
  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash && umbraco.Faqs) {
      const matchingIndex = umbraco.Faqs.findIndex(faq => faq.AnchorTarget === hash)
      if (matchingIndex !== -1) {
        setOpenIndex(matchingIndex)
      }
    }
  }, [umbraco.Faqs])

  const maybeScrollToQuestion = (element) => {
    const headerHeight = 64
    const topPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight
    const elementInView = element.getBoundingClientRect().top >= headerHeight &&
                          element.getBoundingClientRect().bottom <= window.innerHeight
    if (!elementInView) {
      window.scrollTo({ top: topPosition, behavior: 'smooth' })
    }
  }

  const handleQuestionClick = (index) => {
    const isOpening = openIndex !== index
    setOpenIndex(isOpening ? index : null)
    if (isOpening && questionRefs.current[index]) {
      setTimeout(() => {
        maybeScrollToQuestion(questionRefs.current[index]?.querySelector('dt'))
      }, 400)
    }
  }

  return (
    <div
      id={umbraco?.AnchorTarget || null}
      data-theme={theme}
      className='text-[--faq-title]'
      style={{ backgroundColor }}
    >
      <section className='relative !bg-[--faq-bg] px-4 md:px-8 pt-14 pb-16 md:pt-16 md:pb-20 lg:px-10 _block-section _FAQBlock-section'>
        {(umbraco.Title || umbraco.Summary) && (
          <header className='relative z-10 mx-auto w-full xl:max-w-5xl xl:px-6 text-center pb-8'>
            {/* Note to devs this needs to support line break */}
            {umbraco.Title && (
              <h2 className='text-balance text-4xl font-extrabold tracking-tight text md:text-5xl mb-5'>
                {umbraco.Title}
              </h2>
            )}
            {umbraco.Summary && (
              <div
                className='text-base md:text-base'
                dangerouslySetInnerHTML={{ __html: umbraco.Summary }}
              />
            )}
          </header>
        )}
        <div aria-hidden='true' className='absolute inset-0 transform-gpu overflow-hidden'>
          {theme === 'dark' &&
            <div className='absolute border-radius-[800px] left-1/2 -bottom-[540px] md:-bottom-[520px] lg:-bottom-[440px] w-[1000px] h-[800px] bg-blue-rad -translate-x-1/2 blur-2xl opacity-70 pointer-events-none' />}
        </div>
        <div className={`${(umbraco.Title || umbraco.Summary) ? 'mt-0' : '-mt-8'} relative mx-auto w-full max-w-5xl`}>
          <div>
            {umbraco.Faqs.filter(faq => faq.Question && faq.Answer).map((faq, index) => {
              const isOpen = openIndex === index
              return (
                <dl id={faq.AnchorTarget} key={faq.Question} className='mt-0 pt-1.5 border-b-2 border-[--faq-divider]' ref={el => { questionRefs.current[index] = el }}>
                  <dt>
                    <button
                      className='group pt-10 pb-8 flex w-full items-start justify-between text-left text-[--faq-button-text]'
                      onClick={() => handleQuestionClick(index)}
                    >
                      <span className='text-xl font-extrabold text-[--faq-title]'>{faq.Question}</span>
                      <span className='relative ml-6 flex h-7 items-center'>
                        <PlusIcon aria-hidden='true' className={`ease-out duration-400 size-5 _icon-stroke ${isOpen ? 'rotate-45' : ''} text-[--faq-icon]`} />
                      </span>
                    </button>
                  </dt>
                  <dd className={`grid ease-out duration-400 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    {/* this div.overflow-hidden is used for the transition effect */}
                    <div className={`overflow-hidden ease-in duration-400 -mt-4 ${isOpen ? 'opacity-100' : 'opacity-0'}`}>
                      <div
                        className='_rte text-base pr-12 pb-10 text-[--faq-text]'
                        dangerouslySetInnerHTML={{ __html: faq.Answer }}
                      />
                    </div>
                  </dd>
                </dl>
              )
            })}
          </div>
        </div>
        {umbraco.Button && (
          <div className='mx-auto px-4 pt-1'>
            <div className='mt-8 flex items-center justify-center'>
              <Button
                href={umbraco.Button.Url}
                target={umbraco.Button.Target}
                variant='primary'
                size='lg'
              >
                {umbraco.Button.Name}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}

reactMount('FAQBlock', FAQBlock)
